import React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import type { InsightNotification, InsightNotificationLink } from '../notificationTypes';
import { NEEDS_ACTION_TEST_IDS } from '../notificationTestIds';

interface NeedsActionStripProps {
  notifications: InsightNotification[];
  resolveLink?: (link: InsightNotificationLink) => string | null;
  onNavigate?: (path: string) => void;
  onMarkRead?: (notificationId: string) => void;
}

const SEVERITY_TO_ALERT: Record<InsightNotification['severityLabel'], 'error' | 'warning' | 'info'> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

/**
 * Attention-first strip at the top of Reports (AC-025): shows ONLY items that
 * need a decision or intervention — critical anomalies, decision-ready tests,
 * guardrail breaches. Renders nothing when there is nothing to act on.
 */
export const NeedsActionStrip: React.FC<NeedsActionStripProps> = ({
  notifications,
  resolveLink,
  onNavigate,
  onMarkRead,
}) => {
  const actionable = notifications
    .filter(notification => notification.status === 'unread')
    .filter(notification =>
      notification.kind === 'decision_ready' ||
      notification.kind === 'guardrail_breach' ||
      (notification.kind === 'anomaly' && notification.severityLabel === 'critical'))
    .sort((a, b) => b.severity - a.severity);

  if (!actionable.length) {
    return null;
  }

  return (
    <Box data-testid={NEEDS_ACTION_TEST_IDS.strip} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Needs action</Typography>
      <Stack spacing={0.75}>
        {actionable.map(notification => {
          const linkPath = notification.link && resolveLink ? resolveLink(notification.link) : null;
          return (
            <Alert
              key={notification.id}
              severity={SEVERITY_TO_ALERT[notification.severityLabel]}
              data-testid={NEEDS_ACTION_TEST_IDS.item(notification.id)}
              action={(
                <Stack direction="row" spacing={0.5}>
                  {linkPath && (
                    <Button
                      size="small"
                      color="inherit"
                      data-testid={NEEDS_ACTION_TEST_IDS.itemLink(notification.id)}
                      onClick={() => {
                        onMarkRead?.(notification.id);
                        onNavigate?.(linkPath);
                      }}
                    >
                      Open
                    </Button>
                  )}
                  {onMarkRead && (
                    <Button size="small" color="inherit" onClick={() => onMarkRead(notification.id)}>
                      Dismiss
                    </Button>
                  )}
                </Stack>
              )}
            >
              <strong>{notification.title}</strong> — {notification.body}
              {notification.nextAction ? ` Next: ${notification.nextAction}` : ''}
            </Alert>
          );
        })}
      </Stack>
    </Box>
  );
};
